import React, { useState, useEffect } from 'react';
import { terms } from '../constants';
import { marketDynamicsTerms, moduleNames } from '../../constants';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { chevron_small_right } from '@lwt-helix/helix-icon/outlined';
import Loader from '@lwt-helix/loader';
import { getMonthlySupplyDemand } from '../../service/service-gateway';
import { useCommonStore, useSearchStore } from '../../store/store';
import { format, isSameMonth, startOfMonth, subMonths } from 'date-fns';
import { formatUnits } from '../../agent-production/helpers/dataFormatters';
import { getDirection } from '../helpers';
import SupplyAndDemandChart from './SupplyAndDemandChart';
import { getProductURL } from '../../common/helpers/nav';
import { useHistory } from 'react-router-dom';
import { useAuthStore } from '../../store/auth/store';

const SupplyDemand = () => {
    const history = useHistory();
    const userProducts = useCommonStore((state) => state.privileges?.userProducts);
    const isLegacyUser = useAuthStore((state) => state.isLegacyUser);
    const hasProficiencyMetricsAccess = !!(userProducts?.includes(moduleNames.proficiencyMetrics) || isLegacyUser);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [supplyDemandData, setSupplyDemandData] = useState({
        currentMsi: null,
        priorYearMsi: null,
        msiDirection: null,
        currentAvgDom: null,
        priorYearAvgDom: null,
        avgDomDirection: null,
        monthName: null
    });
    const [msiChartData, setMsiChartData] = useState({ msiValues: [] });
    const [avgDomChartData, setAvgDomChartData] = useState({ avgDomValues: [] });
    const [hasData, setHasData] = useState(true);
    const { selectedMls } = useSearchStore(state => ({
        selectedMls: state[state.selectedModule]?.selectedMls,
    }));

    useEffect(() => {
        const fetchData = async () => {
            const selectedMlsId = selectedMls?.mlsId.toString();
            if (!selectedMlsId) return;
            setIsLoadingData(true);
            try {
                const response = await getMonthlySupplyDemand(selectedMlsId);

                if (!response?.results || response.results.length === 0) {
                    setHasData(false);
                    setIsLoadingData(false);
                    return;
                }
                const currentDate = new Date();
                const previousMonth = startOfMonth(subMonths(currentDate, 1));
                const previousYearMonth = startOfMonth(subMonths(previousMonth, 12));

                const chartStartMonth = startOfMonth(subMonths(previousMonth, 11));
                const chartRangeData = (response?.results ?? [])
                    .filter(item => {
                        const itemDate = new Date(item.firstday);
                        return itemDate >= chartStartMonth && itemDate <= previousMonth;
                    })
                    .sort((a, b) => new Date(a.firstday) - new Date(b.firstday));

                const monthName = format(previousMonth, 'MMMM');

                const previousMonthData = response.results.find(item => {
                    const itemDate = new Date(item.firstday);
                    return isSameMonth(itemDate, previousMonth);
                });

                const previousYearData = response.results.find(item => {
                    const itemDate = new Date(item.firstday);
                    return isSameMonth(itemDate, previousYearMonth);
                });

                const currentMsi = previousMonthData?.msi;
                const priorYearMsi = previousYearData?.msi;
                const currentAvgDom = previousMonthData?.avgDom;
                const priorYearAvgDom = previousYearData?.avgDom;

                setSupplyDemandData({
                    currentMsi,
                    priorYearMsi,
                    msiDirection: getDirection(currentMsi, priorYearMsi),
                    currentAvgDom,
                    priorYearAvgDom,
                    avgDomDirection: getDirection(currentAvgDom, priorYearAvgDom),
                    monthName
                });

                setMsiChartData({
                    msiValues: chartRangeData.map(item => item.msi)
                });
                setAvgDomChartData({
                    avgDomValues: chartRangeData.map(item => item.avgDom)
                });

                setHasData(true);
            } catch (error) {
                console.error(error);
                setHasData(false);
            } finally {
                setIsLoadingData(false);
            }
        };
        fetchData();
    }, [selectedMls?.mlsId]);

    const emptyState = (
        <div className="d-flex flex-column justify-content-center align-items-center py-5">
            <p className="helix-gray-6 text-center mb-0">
                {terms.emptySupplyAndDemandData}
            </p>
        </div>
    );

    const metricsContent = (
        <div className="d-flex flex-column supply-demand-content">
            <div className="d-flex align-items-start mb-4">
                <div className="mr-4 supply-demand-chart d-flex justify-content-center align-items-center">
                    <SupplyAndDemandChart chartData={msiChartData} />
                </div>
                <div className="supply-demand-text">
                    <h4 className="d-flex supply-demand-subtitle mb-2 justify-content-start">
                        {terms.supplyOfInventoryTitle(formatUnits(supplyDemandData.currentMsi))}
                    </h4>
                    <p className="supply-demand-metrics-text helix-gray-6 mb-0">
                        {terms.supplyOfInventory(supplyDemandData.monthName,
                            formatUnits(supplyDemandData.currentMsi),
                            supplyDemandData.msiDirection)
                        }
                    </p>
                </div>
            </div>
            <div className="d-flex align-items-start">
                <div className="d-flex mr-4 supply-demand-chart align-items-center justify-content-center">
                    <SupplyAndDemandChart chartData={avgDomChartData} />
                </div>
                <div className="supply-demand-text">
                    <h4 className="d-flex supply-demand-subtitle mb-2 justify-content-start">
                        {terms.averageDaysOnMarketTitle(formatUnits(supplyDemandData.currentAvgDom))}
                    </h4>
                    <p className="supply-demand-metrics-text helix-gray-6 mb-0">
                        {terms.averageDaysOnMarket(supplyDemandData.monthName,
                            formatUnits(supplyDemandData.currentAvgDom),
                            supplyDemandData.avgDomDirection)
                        }
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="content dashboard-card d-flex flex-column h-100 p-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div className='d-flex align-items-center'>
                    <h2 className="helix-legend-value mb-1 mr-2">{terms.supplyAmpDemand}</h2>
                </div>
                {hasProficiencyMetricsAccess && (
                    <button
                        className="helix-body--strong dashboard-nav-btn helix-brand-color"
                        onClick={() =>
                            history.push(getProductURL({product: moduleNames.marketDynamics, userProducts, isLegacyUser}))
                        }
                    >
                        {marketDynamicsTerms.marketDynamics}
                        <HelixIcon icon={chevron_small_right} className="ml-1" />
                    </button>
                )}
            </div>
            {isLoadingData ? (
                <div className="d-flex justify-content-center align-items-center pb-4 recruiting-card-size">
                    <Loader />
                </div>
            ) : hasData ? metricsContent : emptyState}
        </div>
    );
};

export default SupplyDemand;
